import { useEffect, useState, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import {
  handleInputChangeWithScroll,
  MESSAGE_TYPES,
} from "../../../utils/editUtils";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import { useParams, useNavigate } from "react-router-dom";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  executeValidation,
  handleInputChangeWithValidation,
  handleResetWithValidation,
  clearValidationErrors,
} from "../../../utils/formValidation";

function StudioEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const contentKey = "studioContent"; // ページのキー
  const [pageContent, setPageContent] = useState({});
  const [originalSlug, setOriginalSlug] = useState(""); // Iframe表示用の元のslug
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [mainImage, setMainImage] = useState(null); // top_image_id
  const [visualImages, setVisualImages] = useState([]); // image_ids
  const [studiosList, setStudiosList] = useState([]); // スタジオリスト
  const [isNewStudio, setIsNewStudio] = useState(false); // 新規作成モード
  const [isPublished, setIsPublished] = useState(false); // 公開状態
  const [breadcrumbs, setBreadcrumbs] = useState([]); // パンくず
  const { studioId } = useParams();
  const navigate = useNavigate();

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // ファイルアップロードハンドラー
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }
      
      setLoadingText("アップロード中");
      
      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
      
      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        // アップロード完了後に選択状態にする
        if (data.images?.[0]) {
          // APIレスポンスをMediaGallery形式に変換
          const uploadedImage = {
            id: data.images[0].id,
            image_url: data.images[0].url || data.images[0].image_url,
            mime_type: data.images[0].mime_type,
            width: data.images[0].width,
            height: data.images[0].height,
            file_size: data.images[0].file_size,
          };
          
          // メディアファイル一覧を再取得
          await fetchMediaFiles();
          
          // fetchMediaFiles完了後に選択状態にする
          selectMedia(uploadedImage, true);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia, setLoadingText]
  );

  // スタジオリストの取得
  const fetchStudiosList = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studios/`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStudiosList(data);
    } catch (error) {
      console.error("スタジオリストの取得に失敗しました", error);
    }
  }, []);

  // スタジオデータの取得
  const fetchStudioData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studio/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          studio_id: studioId,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setPageContent(data);
      setOriginalSlug(data.slug); // Iframe表示用の元のslugを保持
      setMainImage(data.main_image);
      setVisualImages(data.images);
      setIsPublished(data.is_published);
      setBreadcrumbs([
        { title: "Home", path: `/` },
        { title: `${data.slug}`, path: `/studio/${data.id}` },
      ]);
      setHasChanges(false);

      // iframeにも通知して初期データを設定
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.TEXT_UPDATE,
              contentKey,
              studioContent: data,
              allData: { studioContent: data },
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 100);
    } catch (error) {
      console.error("詳細の取得に失敗しました", error);
    }
    setLoadingText("");
  }, [studioId, contentKey]);

  // 初回のみ実行（スタジオリストとメディアファイル取得）
  useEffect(() => {
    fetchStudiosList();
    fetchMediaFiles();

    // URLが /studio/new の場合は新規作成モードに設定
    if (studioId === "new") {
      setIsNewStudio(true);
      const newPageContent = {
        name: "",
        slug: "",
        address: "",
        lat: "",
        lng: "",
      };
      setPageContent(newPageContent);
      setOriginalSlug("new"); // 新規作成時はnewを設定
      setMainImage(null);
      setVisualImages([]);
      setIsPublished(false);
      setBreadcrumbs([
        { title: "Home", path: `/` },
        { title: "Add Studio", path: `/studio/new` },
      ]);
      setHasChanges(false);
    }
  }, [fetchStudiosList, fetchMediaFiles, studioId]);

  // studioIdが変更された時に実行（スタジオデータ取得）
  useEffect(() => {
    // バリデーションエラーをクリア
    clearValidationErrors();

    if (studioId && !isNewStudio) {
      fetchStudioData();
    }
  }, [studioId, fetchStudioData, isNewStudio]);

  // スタジオ選択ハンドラー
  const handleStudioSelect = useCallback(
    (selectedStudioId) => {
      if (selectedStudioId === "new") {
        // バリデーションエラーをクリア
        clearValidationErrors();

        // 新規作成モードに切り替え
        setIsNewStudio(true);
        navigate("/studio/new");
        const newPageContent = {
          name: "",
          slug: "",
          address: "",
          lat: "",
          lng: "",
        };
        setPageContent(newPageContent);
        setOriginalSlug("new"); // 新規作成時はnewを設定
        setMainImage(null);
        setVisualImages([]);
        setIsPublished(false);
        setBreadcrumbs([
          { title: "Home", path: `/` },
          { title: "Add Studio", path: `/studio/new` },
        ]);
        setHasChanges(false);

        // iframeに空データを送信
        setTimeout(() => {
          const iframe = document.querySelector("iframe");
          if (iframe && iframe.contentWindow) {
            // テキストデータの更新
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                contentKey,
                studioContent: newPageContent,
                allData: { studioContent: newPageContent },
              },
              process.env.REACT_APP_ORIGIN
            );

            // メイン画像をクリア
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.IMAGE_UPDATE,
                target: `.edit_main_image`,
                contentKey,
                fieldName: "main_image",
                value: null,
                allData: {
                  studioContent: { ...newPageContent, main_image: null },
                },
              },
              process.env.REACT_APP_ORIGIN
            );

            const componentKey = "studio";

            // ビジュアル画像をクリア
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.MULTIPLE_IMAGES_UPDATE,
                target: `.edit_visual_images`,
                contentKey,
                componentKey,
                fieldName: "visual_images",
                value: [],
              },
              process.env.REACT_APP_ORIGIN
            );
          }
        }, 100);
      } else if (selectedStudioId !== parseInt(studioId)) {
        // バリデーションエラーをクリア
        clearValidationErrors();
        setIsNewStudio(false);
        navigate(`/studio/${selectedStudioId}`);
      } else {
        // バリデーションエラーをクリア
        clearValidationErrors();
        fetchStudioData();
      }
    },
    [studioId, navigate, fetchStudioData]
  );

  // ビジュアル画像変更ハンドラー
  const handleVisualImagesChange = useCallback(
    (images) => {
      console.log("Visual images changed:", images);
      setVisualImages(images);
      setHasChanges(true);

      // iframeに通知
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.MULTIPLE_IMAGES_UPDATE,
              target: `.edit_visual_images`,
              contentKey,
              fieldName: "visual_images",
              value: images,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 100);
    },
    [contentKey]
  );

  // メイン画像変更ハンドラー
  const handleMainImageChange = useCallback(
    (image) => {
      console.log("Main image changed:", image);
      setMainImage(image);
      setHasChanges(true);

      // iframeに通知
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          const targetSelector = `.edit_main_image`;

          // 画像更新メッセージ
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.IMAGE_UPDATE,
              target: targetSelector,
              contentKey,
              fieldName: "main_image",
              value: image,
              allData: { studioContent: { ...pageContent, main_image: image } },
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 100);
    },
    [contentKey, pageContent]
  );

  // スタジオデータを保存（新規作成・更新）
  const handleStudioDataSubmit = async () => {
    // 保存するデータ
    const formData = {
      name: pageContent.name,
      address: pageContent.address,
      slug: pageContent.slug,
      image_ids: visualImages.map((img) => img.id),
      top_image: mainImage ? mainImage.id : null,
    };

    // 更新の場合はstudio_idを追加
    if (!isNewStudio) {
      formData.studio_id = studioId;
    }

    // バリデーション実行
    const allFormData = {
      ...pageContent,
    };

    if (
      !executeValidation(allFormData, validationRules, fieldLabels, setMessage)
    ) {
      return;
    }

    setLoadingText("緯度経度を取得中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;

      // 緯度経度を取得
      const locationData = {
        address: pageContent.address,
        studio_id: isNewStudio ? null : studioId,
      };

      const locationResponse = await fetch(`${apiUrl}/api/get_location/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(locationData),
      });

      const locationResult = await locationResponse.json();
      if (!locationResponse.ok) {
        throw new Error(
          `${locationResult.message} status: ${locationResponse.status}`
        );
      }

      // 緯度経度をformDataに追加
      formData.lat = locationResult.location.lat;
      formData.lng = locationResult.location.lng;

      setLoadingText(isNewStudio ? "作成中" : "保存中");

      const endpoint = isNewStudio
        ? "/api/create_studio/"
        : "/api/update_studio/";

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }

      setMessage(data.message);
      setHasChanges(false);

      if (isNewStudio && data.studio_id) {
        // 新規作成の場合、作成されたスタジオのページに遷移
        setIsNewStudio(false);
        await fetchStudiosList();
        navigate(`/studio/${data.studio_id}`);
      } else {
        // 更新の場合は最新データを取得
        await fetchStudiosList();
        await fetchStudioData();
      }
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
    setLoadingText("");
  };

  // バリデーションルールを定義
  const validationRules = {
    name: { required: true },
    slug: { required: true },
    address: { required: true },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    name: "店舗名(日本語)",
    slug: "店舗名(英語)",
    address: "住所",
  };

  // テキスト入力ハンドラー
  const handleInputChange = (e) => {
    handleInputChangeWithValidation(
      e,
      setPageContent,
      handleInputChangeWithScroll,
      {
        contentKey,
        currentData: pageContent,
        setData: setPageContent,
      }
    );
    setHasChanges(true);
  };

  // スタジオ削除ハンドラー
  const handleDeleteStudio = async () => {
    // 削除確認
    if (!window.confirm(`${pageContent.name}校を本当に削除しますか？`)) {
      return;
    }

    setLoadingText("削除中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/delete_studio/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          studio_id: studioId,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }

      setMessage(data.message);

      // スタジオリストを更新
      await fetchStudiosList();

      // 削除後、スタジオリストの最初のスタジオを選択
      const updatedStudiosList = await getUpdatedStudiosList();
      if (updatedStudiosList.length > 0) {
        const firstStudio = updatedStudiosList[0];
        navigate(`/studio/${firstStudio.id}`);
      } else {
        // スタジオが一つもない場合は新規作成モードに
        navigate("/studio/new");
      }
    } catch (error) {
      console.error("削除に失敗しました:", error);
      alert("削除に失敗しました。");
    }

    setLoadingText("");
  };

  // 更新されたスタジオリストを取得
  const getUpdatedStudiosList = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studios/`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("スタジオリストの取得に失敗しました", error);
      return [];
    }
  };

  // 公開/非公開トグルハンドラー
  const handlePublishToggle = async (e) => {
    const newPublishStatus = e.target.checked;

    // 新規作成モードの場合は状態のみ更新
    if (isNewStudio) {
      setIsPublished(newPublishStatus);
      return;
    }

    const confirmMessage = newPublishStatus
      ? "公開してよろしいですか？"
      : "非公開にしてよろしいですか？";
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) {
      return;
    }

    setLoadingText("公開状態を更新中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_is_published/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          studio_id: studioId,
          is_published: newPublishStatus,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }

      setIsPublished(newPublishStatus);
      setMessage(
        newPublishStatus
          ? "スタジオを公開しました。"
          : "スタジオを非公開にしました。"
      );
    } catch (error) {
      console.error("公開状態の更新に失敗しました:", error);
      alert("公開状態の更新に失敗しました。");
    }

    setLoadingText("");
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            {/* 編集フォーム */}
            <div className="form">
              {/* スタジオリスト */}
              <div className="studio-list">
                <ul>
                  <li
                    className={isNewStudio ? "active" : ""}
                    onClick={() => handleStudioSelect("new")}
                    style={{ cursor: "pointer" }}
                  >
                    + Add New Studio
                  </li>
                  {studiosList.map((studio) => (
                    <li
                      key={studio.id}
                      className={
                        parseInt(studioId) === studio.id && !isNewStudio
                          ? "active"
                          : ""
                      }
                      onClick={() => handleStudioSelect(studio.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {studio.name}
                    </li>
                  ))}
                </ul>
              </div>
              {/* 基本情報 */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      {/* 店舗名(日本語) */}
                      <input
                        className="input4"
                        type="text"
                        name="name"
                        value={pageContent?.name || ""}
                        placeholder="店舗名を入力"
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="店舗名(日本語)"
                        data-required="true"
                      />

                      {/* 店舗名(英語) */}
                      <input
                        className="input4"
                        type="text"
                        name="slug"
                        value={pageContent?.slug || ""}
                        placeholder="英語表記を入力"
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        data-label="店舗名(英語)"
                        data-required="true"
                      />

                      {/* メイン画像 */}
                      <div className="input3 img-form">
                        <MediaSelector
                          label="メイン画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="main_image"
                          selectedMedia={mainImage}
                          onSelect={handleMainImageChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 住所情報 */}
              <section>
                <div className="area1">
                  {/* 住所 */}
                  <input
                    type="text"
                    name="address"
                    value={pageContent?.address || ""}
                    placeholder="住所を入力"
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="住所"
                    data-required="true"
                  />
                </div>
              </section>

              {/* ビジュアル画像 */}
              <section>
                <div className="img-form">
                  <MediaSelector
                    label="ビジュアル画像"
                    aspectRatio="16:9"
                    type="image"
                    fieldName="visual_images"
                    selectedMedia={visualImages}
                    onSelect={handleVisualImagesChange}
                    mode="multiple"
                  />
                </div>
              </section>

              {/* 公開/非公開 */}
              <section>
                <div className="area1">
                  <div className="publish-toggle">
                    <label>
                      <input
                        type="checkbox"
                        checked={isPublished}
                        onChange={handlePublishToggle}
                      />
                      <span>公開する</span>
                    </label>
                  </div>
                </div>
              </section>

              {/* 削除 */}
              {!isNewStudio && (
                <button
                  type="button"
                  className="delete-btn"
                  onClick={handleDeleteStudio}
                >
                  Delete
                </button>
              )}
            </div>
            {/* 送信ボタン */}
            <div className="submit-area">
              <button
                type="reset"
                onClick={
                  isNewStudio
                    ? () => {
                        handleResetWithValidation(() => {
                          setPageContent({
                            name: "",
                            slug: "",
                            address: "",
                            lat: "",
                            lng: "",
                          });
                          setMainImage(null);
                          setVisualImages([]);
                          setIsPublished(false);
                          setHasChanges(false);
                        });
                      }
                    : () => handleResetWithValidation(fetchStudioData)
                }
                disabled={!hasChanges}
              >
                Reset
              </button>
              <button
                type="submit"
                onClick={handleStudioDataSubmit}
                disabled={!hasChanges}
              >
                Submit
              </button>
            </div>
            {/* メディア / オーバーレイ */}
            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>
      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}
      {/* プレビュー */}
      <Iframe
        path={
          isNewStudio
            ? "/studio/new?secret=this_is_a_request_from_the_admin_page"
            : originalSlug
            ? `/studio/${originalSlug}`
            : "/"
        }
        data={pageContent}
        contentKey={contentKey}
      />
    </>
  );
}

// MediaProviderでラップしたコンポーネントをエクスポート
export default function StudioEditPage() {
  return (
    <MediaProvider>
      <StudioEditPageContent />
    </MediaProvider>
  );
}
